import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// localStorage keys
const LS_ORG_ID = 'aimod_selected_org_id';
const LS_ORG_NAME = 'aimod_selected_org_name';
const LS_ORG_SLUG = 'aimod_selected_org_slug';
const LS_PROJECT_ID = 'aimod_selected_project_id';
const LS_PROJECT_NAME = 'aimod_selected_project_name';
const LS_PROJECT_SLUG = 'aimod_selected_project_slug';

const OrgProjectContext = createContext(undefined);

/**
 * Reads org and project slugs from the current URL query parameters.
 * Returns { orgSlug, projectSlug } or nulls if not present.
 */
function readSlugsFromURL() {
  const params = new URLSearchParams(window.location.search);
  return {
    orgSlug: params.get('org') || null,
    projectSlug: params.get('project') || null,
  };
}

/**
 * Updates the URL query parameters with org/project slugs without triggering navigation.
 * Uses replaceState to avoid polluting browser history on every context switch.
 */
function updateURLWithSlugs(orgSlug, projectSlug) {
  const params = new URLSearchParams(window.location.search);

  if (orgSlug) {
    params.set('org', orgSlug);
  } else {
    params.delete('org');
  }

  if (projectSlug) {
    params.set('project', projectSlug);
  } else {
    params.delete('project');
  }

  const newSearch = params.toString();
  const newURL = newSearch
    ? `${window.location.pathname}?${newSearch}`
    : window.location.pathname;

  window.history.replaceState(null, '', newURL);
}

/**
 * Resolves org and project slugs to their full records via the API.
 * Returns { org, project } objects or nulls if not found.
 */
async function resolveFromSlugs(orgSlug, projectSlug) {
  try {
    // Fetch all orgs and find by slug
    const orgsRes = await apiFetch(`/api/orgs?per_page=200`);
    if (!orgsRes.ok) return { org: null, project: null };

    const orgsData = await orgsRes.json();
    const orgs = Array.isArray(orgsData) ? orgsData : (orgsData.data || orgsData.results || orgsData.items || []);
    const org = orgs.find(o => o.slug === orgSlug);
    if (!org) return { org: null, project: null };

    if (!projectSlug) return { org, project: null };

    // Fetch projects for this org and find by slug
    const projRes = await apiFetch(`/api/orgs/${org.id}/projects`);
    if (!projRes.ok) return { org, project: null };

    const projData = await projRes.json();
    const projects = Array.isArray(projData) ? projData : (projData.data || projData.results || projData.items || []);
    const project = projects.find(p => p.slug === projectSlug);

    return { org, project: project || null };
  } catch {
    return { org: null, project: null };
  }
}

/**
 * Validates that an org exists by calling the API.
 * Returns the org object if found, null if 404 or error.
 */
async function validateOrg(orgId) {
  try {
    const res = await apiFetch(`/api/orgs/${orgId}`);
    if (res.ok) {
      return await res.json();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Validates that a project exists within an org by calling the API.
 * Returns the project object if found, null if 404 or error.
 */
async function validateProject(orgId, projectId) {
  try {
    const res = await apiFetch(`/api/orgs/${orgId}/projects/${projectId}`);
    if (res.ok) {
      return await res.json();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetches the default org and project (first available or one named "Default").
 * Returns { orgId, orgName, orgSlug, projectId, projectName, projectSlug } or nulls if none found.
 */
async function fetchDefaults() {
  try {
    const orgsRes = await apiFetch(`/api/orgs`);
    if (!orgsRes.ok) return { orgId: null, orgName: null, orgSlug: null, projectId: null, projectName: null, projectSlug: null };

    const orgsData = await orgsRes.json();
    const orgs = Array.isArray(orgsData) ? orgsData : (orgsData.data || orgsData.results || orgsData.items || []);

    if (orgs.length === 0) {
      return { orgId: null, orgName: null, orgSlug: null, projectId: null, projectName: null, projectSlug: null };
    }

    // Prefer org named "Default", otherwise use first
    const defaultOrg = orgs.find(o => o.name === 'Default') || orgs[0];

    // Fetch projects for the default org
    const projRes = await apiFetch(`/api/orgs/${defaultOrg.id}/projects`);
    if (!projRes.ok) {
      return { orgId: defaultOrg.id, orgName: defaultOrg.name, orgSlug: defaultOrg.slug, projectId: null, projectName: null, projectSlug: null };
    }

    const projData = await projRes.json();
    const projects = Array.isArray(projData) ? projData : (projData.data || projData.results || projData.items || []);

    if (projects.length === 0) {
      return { orgId: defaultOrg.id, orgName: defaultOrg.name, orgSlug: defaultOrg.slug, projectId: null, projectName: null, projectSlug: null };
    }

    // Prefer project named "Default", otherwise use first
    const defaultProject = projects.find(p => p.name === 'Default') || projects[0];

    return {
      orgId: defaultOrg.id,
      orgName: defaultOrg.name,
      orgSlug: defaultOrg.slug,
      projectId: defaultProject.id,
      projectName: defaultProject.name,
      projectSlug: defaultProject.slug,
    };
  } catch {
    return { orgId: null, orgName: null, orgSlug: null, projectId: null, projectName: null, projectSlug: null };
  }
}

export function OrgProjectProvider({ children }) {
  const [orgId, setOrgId] = useState(null);
  const [orgName, setOrgName] = useState(null);
  const [orgSlug, setOrgSlug] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState(null);
  const [projectSlug, setProjectSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  // Context version increments on every org/project change to trigger downstream reloads
  const [contextVersion, setContextVersion] = useState(0);
  const initializedRef = useRef(false);

  // On mount: check URL slugs first, then localStorage, then fall back to defaults
  useEffect(() => {
    let cancelled = false;

    async function init() {
      // Priority 1: URL query params (allows shareable links)
      const { orgSlug: urlOrgSlug, projectSlug: urlProjectSlug } = readSlugsFromURL();

      if (urlOrgSlug) {
        const { org, project } = await resolveFromSlugs(urlOrgSlug, urlProjectSlug);
        if (org && !cancelled) {
          setOrgId(org.id);
          setOrgName(org.name);
          setOrgSlug(org.slug);
          localStorage.setItem(LS_ORG_ID, org.id);
          localStorage.setItem(LS_ORG_NAME, org.name);
          localStorage.setItem(LS_ORG_SLUG, org.slug);

          if (project) {
            setProjectId(project.id);
            setProjectName(project.name);
            setProjectSlug(project.slug);
            localStorage.setItem(LS_PROJECT_ID, project.id);
            localStorage.setItem(LS_PROJECT_NAME, project.name);
            localStorage.setItem(LS_PROJECT_SLUG, project.slug);
          }
          // Update URL to ensure it's clean (in case slug resolution changed anything)
          updateURLWithSlugs(org.slug, project ? project.slug : null);
          initializedRef.current = true;
          setLoading(false);
          return;
        }
        // URL slugs didn't resolve — fall through to localStorage/defaults
      }

      // Priority 2: localStorage
      const storedOrgId = localStorage.getItem(LS_ORG_ID);
      const storedOrgName = localStorage.getItem(LS_ORG_NAME);
      const storedOrgSlug = localStorage.getItem(LS_ORG_SLUG);
      const storedProjectId = localStorage.getItem(LS_PROJECT_ID);
      const storedProjectName = localStorage.getItem(LS_PROJECT_NAME);
      const storedProjectSlug = localStorage.getItem(LS_PROJECT_SLUG);

      if (storedOrgId && storedProjectId) {
        // Validate stored org still exists
        const org = await validateOrg(storedOrgId);
        if (org && !cancelled) {
          // Validate stored project still exists within this org
          const project = await validateProject(storedOrgId, storedProjectId);
          if (project && !cancelled) {
            setOrgId(storedOrgId);
            setOrgName(storedOrgName || org.name);
            setOrgSlug(storedOrgSlug || org.slug);
            setProjectId(storedProjectId);
            setProjectName(storedProjectName || project.name);
            setProjectSlug(storedProjectSlug || project.slug);
            // Sync URL with current selection
            updateURLWithSlugs(storedOrgSlug || org.slug, storedProjectSlug || project.slug);
            initializedRef.current = true;
            setLoading(false);
            return;
          }
        }
      }

      // Priority 3: Fall back to defaults
      if (!cancelled) {
        const defaults = await fetchDefaults();
        setOrgId(defaults.orgId);
        setOrgName(defaults.orgName);
        setOrgSlug(defaults.orgSlug);
        setProjectId(defaults.projectId);
        setProjectName(defaults.projectName);
        setProjectSlug(defaults.projectSlug);

        // Persist defaults to localStorage
        if (defaults.orgId) {
          localStorage.setItem(LS_ORG_ID, defaults.orgId);
          localStorage.setItem(LS_ORG_NAME, defaults.orgName || '');
          localStorage.setItem(LS_ORG_SLUG, defaults.orgSlug || '');
        }
        if (defaults.projectId) {
          localStorage.setItem(LS_PROJECT_ID, defaults.projectId);
          localStorage.setItem(LS_PROJECT_NAME, defaults.projectName || '');
          localStorage.setItem(LS_PROJECT_SLUG, defaults.projectSlug || '');
        }
        // Sync URL
        updateURLWithSlugs(defaults.orgSlug, defaults.projectSlug);
        initializedRef.current = true;
        setLoading(false);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const setOrg = useCallback((newOrgId, newOrgName, newOrgSlug) => {
    setOrgId(newOrgId);
    setOrgName(newOrgName);
    setOrgSlug(newOrgSlug || null);
    // Clear project when org changes — caller should set project separately
    setProjectId(null);
    setProjectName(null);
    setProjectSlug(null);

    if (newOrgId) {
      localStorage.setItem(LS_ORG_ID, newOrgId);
      localStorage.setItem(LS_ORG_NAME, newOrgName || '');
      localStorage.setItem(LS_ORG_SLUG, newOrgSlug || '');
    } else {
      localStorage.removeItem(LS_ORG_ID);
      localStorage.removeItem(LS_ORG_NAME);
      localStorage.removeItem(LS_ORG_SLUG);
    }
    localStorage.removeItem(LS_PROJECT_ID);
    localStorage.removeItem(LS_PROJECT_NAME);
    localStorage.removeItem(LS_PROJECT_SLUG);

    // Update URL — project cleared, only org slug
    updateURLWithSlugs(newOrgSlug || null, null);

    // Increment context version to trigger downstream reloads
    setContextVersion(v => v + 1);
  }, []);

  const setProject = useCallback((newProjectId, newProjectName, newProjectSlug) => {
    setProjectId(newProjectId);
    setProjectName(newProjectName);
    setProjectSlug(newProjectSlug || null);

    if (newProjectId) {
      localStorage.setItem(LS_PROJECT_ID, newProjectId);
      localStorage.setItem(LS_PROJECT_NAME, newProjectName || '');
      localStorage.setItem(LS_PROJECT_SLUG, newProjectSlug || '');
    } else {
      localStorage.removeItem(LS_PROJECT_ID);
      localStorage.removeItem(LS_PROJECT_NAME);
      localStorage.removeItem(LS_PROJECT_SLUG);
    }

    // Update URL with both org and project slugs
    updateURLWithSlugs(orgSlug, newProjectSlug || null);

    // Increment context version to trigger downstream reloads
    setContextVersion(v => v + 1);
  }, [orgSlug]);

  /**
   * Syncs the context from route params (orgId, projectId).
   * Pages with :orgId/:projectId in their route should call this on mount
   * to ensure the context reflects the URL rather than stale localStorage.
   */
  const syncFromRouteParams = useCallback(async (routeOrgId, routeProjectId) => {
    if (!routeOrgId) return;

    // If context already matches, nothing to do
    if (routeOrgId === orgId && (!routeProjectId || routeProjectId === projectId)) {
      return;
    }

    // Validate and set the org from route param
    const org = await validateOrg(routeOrgId);
    if (!org) return; // invalid org ID in URL — don't change context

    setOrgId(org.id);
    setOrgName(org.name);
    setOrgSlug(org.slug || null);
    localStorage.setItem(LS_ORG_ID, org.id);
    localStorage.setItem(LS_ORG_NAME, org.name || '');
    localStorage.setItem(LS_ORG_SLUG, org.slug || '');

    if (routeProjectId) {
      const project = await validateProject(org.id, routeProjectId);
      if (project) {
        setProjectId(project.id);
        setProjectName(project.name);
        setProjectSlug(project.slug || null);
        localStorage.setItem(LS_PROJECT_ID, project.id);
        localStorage.setItem(LS_PROJECT_NAME, project.name || '');
        localStorage.setItem(LS_PROJECT_SLUG, project.slug || '');
        updateURLWithSlugs(org.slug, project.slug);
      } else {
        // Project not found — clear project selection
        setProjectId(null);
        setProjectName(null);
        setProjectSlug(null);
        localStorage.removeItem(LS_PROJECT_ID);
        localStorage.removeItem(LS_PROJECT_NAME);
        localStorage.removeItem(LS_PROJECT_SLUG);
        updateURLWithSlugs(org.slug, null);
      }
    } else {
      // No project in route — clear project selection
      setProjectId(null);
      setProjectName(null);
      setProjectSlug(null);
      localStorage.removeItem(LS_PROJECT_ID);
      localStorage.removeItem(LS_PROJECT_NAME);
      localStorage.removeItem(LS_PROJECT_SLUG);
      updateURLWithSlugs(org.slug, null);
    }

    setContextVersion(v => v + 1);
  }, [orgId, projectId]);

  const value = {
    orgId,
    projectId,
    orgName,
    projectName,
    orgSlug,
    projectSlug,
    setOrg,
    setProject,
    syncFromRouteParams,
    loading,
    contextVersion,
  };

  return (
    <OrgProjectContext.Provider value={value}>
      {children}
    </OrgProjectContext.Provider>
  );
}

export function useOrgProject() {
  const context = useContext(OrgProjectContext);
  if (context === undefined) {
    throw new Error('useOrgProject must be used within an OrgProjectProvider');
  }
  return context;
}

export { OrgProjectContext };
export default OrgProjectContext;
